import { Box, Text } from '@rsos/components/capstone/base';
import { EMERGENCY_CALLS, NON_CALLS } from '@rsos/constants/incidentTypes';
import { DARK_THEME } from '@rsos/theme/constants';
import { getTheme } from '@rsos/theme/themes';
import getCallStatusName from '@rsos/utils/getCallStatusName';
import { formatInternationalPhoneNumberHuman } from '@rsos/utils/internationalPhoneNumberUtils';
import { convertUnixTimestamp } from '@rsos/utils/timeHelpers';
import { CallStatus, CallStatusTag } from './InfoWindow.styles';
import styles from './InfoWindowContent.module.scss';

/**
 * Builds the wrapper className for the info window content. In dark mode the
 * `info-window-dark` marker tells the global stylesheet to recolor the Google
 * Maps bubble chrome (see InfoWindowContent.module.scss).
 */
const getWrapperClassName = (isDark: boolean) =>
  isDark ? `${styles.content} info-window-dark` : styles.content;

interface EmergencyCallData {
  caller_id: string;
  call_start_time: number;
  location_time?: number;
}

interface StatusUpdate {
  update: string;
  timestamp: string;
}

interface TripData {
  trip_reason: string;
}

interface NonCallData {
  body?: {
    status?: StatusUpdate[];
    trip_data?: TripData;
  };
}

interface InfoWindowContentProps {
  type: string;
  data: EmergencyCallData | NonCallData;
  callStatus?: string;
  themeName?: string;
}

/**
 * Wrapper component for Google Maps info window content.
 * This is an uncontrolled component, no access to the redux store or themes,
 * props should be passed down.
 */
const InfoWindowContent = ({
  type,
  data,
  callStatus,
  themeName = 'light',
}: InfoWindowContentProps) => {
  const isDark = themeName === DARK_THEME;

  if (type === EMERGENCY_CALLS) {
    const theme = getTheme(themeName);
    const { caller_id, call_start_time, location_time } =
      data as EmergencyCallData;
    const formattedCallerID = formatInternationalPhoneNumberHuman(caller_id);

    const startTime = Number(location_time || call_start_time);

    const callStatusDisplayName = getCallStatusName(callStatus);

    return (
      <Box className={getWrapperClassName(isDark)}>
        <Text
          marginBottom={'4px'}
          color={theme.colors.primaryText}
          fontWeight={'semibold'}
          size={'large'}
        >
          {formattedCallerID}
        </Text>
        <Text
          marginBottom={'8px'}
          color={theme.colors.secondaryText}
          size={'normal'}
        >
          {convertUnixTimestamp(startTime, true)}
        </Text>

        {callStatusDisplayName && (
          <CallStatusTag status={callStatus} isCallPopup={true} theme={theme}>
            <CallStatus
              data-name="call-status"
              status={callStatus}
              theme={theme}
            >
              {callStatusDisplayName}
            </CallStatus>
          </CallStatusTag>
        )}
      </Box>
    );
  }
  if (type === NON_CALLS) {
    const theme = getTheme(themeName);
    const { status, trip_data } = (data as NonCallData).body || {};
    const latestStatus = status?.[status.length - 1];

    return (
      <Box grow className={getWrapperClassName(isDark)}>
        {latestStatus ? (
          <>
            <Text
              marginBottom={'4px'}
              color={theme.colors.primaryText}
              fontWeight={'semibold'}
              size={'large'}
            >
              {latestStatus.update}
            </Text>
            <Text
              marginBottom={'8px'}
              color={theme.colors.secondaryText}
              size={'normal'}
            >
              {new Date(latestStatus.timestamp).toLocaleTimeString()}
            </Text>
          </>
        ) : (
          <Text
            marginBottom={'8px'}
            color={theme.colors.secondaryText}
            size={'normal'}
          >
            Pending
          </Text>
        )}

        {trip_data && (
          <CallStatus data-name="non-call-status" theme={theme}>
            {trip_data.trip_reason}
          </CallStatus>
        )}
      </Box>
    );
  }
  return null;
};

export default InfoWindowContent;
